import { UserManager, WebStorageStateStore, User } from 'oidc-client-ts';
import { USER_TOKEN_KEY, USER_REFRESH_TOKEN_KEY } from './authTokens';
import { clearAllDrafts } from '../hooks/useDraft';
import { endImpersonation, ImpersonationEndedError, isImpersonating } from './impersonation';

// Issuer OIDC-провайдера (amazing_auth). Discovery: `${issuer}/.well-known/openid-configuration`
const ISSUER       = process.env.REACT_APP_OIDC_ISSUER    ?? 'http://localhost:3002/oidc';
const CLIENT_ID    = process.env.REACT_APP_OIDC_CLIENT_ID ?? 'amazing_frontend';
const REDIRECT_URI = process.env.REACT_APP_OIDC_REDIRECT_URI
  ?? `${window.location.origin}/auth/callback`;
// Отдельный redirect для тихого обновления токена (prompt=none в скрытом iframe).
// ВАЖНО: страница /auth/silent вызывает ТОЛЬКО signinSilentCallback() и не трогает
// authorization code основного логина — иначе возникает гонка за одноразовый code
// (со страницей /auth/callback) и обновление токена ломается (401/«битый» токен).
const SILENT_REDIRECT = process.env.REACT_APP_OIDC_SILENT_REDIRECT_URI
  ?? `${window.location.origin}/auth/silent`;
const POST_LOGOUT  = process.env.REACT_APP_OIDC_POST_LOGOUT_URI
  ?? window.location.origin;

export const userManager = new UserManager({
  authority:                ISSUER,
  client_id:                CLIENT_ID,
  redirect_uri:             REDIRECT_URI,
  silent_redirect_uri:      SILENT_REDIRECT,
  post_logout_redirect_uri: POST_LOGOUT,
  response_type:            'code',          // authorization_code + PKCE (PKCE включается автоматически)
  scope:                    'openid profile email roles offline_access',
  // Встроенный авторенью зовёт signinSilent в обход single-flight ниже и при
  // ротации refresh-токена сталкивался с обновлением из adminApi после 401 —
  // грант отзывался, и страница уходила на логин. Ренью по таймеру — ниже,
  // через renewTokenSilent.
  automaticSilentRenew:     false,
  // Таймаут скрытого iframe (fallback, когда нет refresh-токена). Дефолтные 10 с
  // при неудаче iframe (блокировка сторонних cookie и т.п.) давали 10-секундный
  // экран «Загрузка...» после выгрузки вкладки браузером. Основной путь —
  // refresh-токен — от этого таймаута не зависит.
  silentRequestTimeoutInSeconds: 5,
  userStore:                new WebStorageStateStore({ store: window.localStorage }),
});

// ─── Single-flight silent renew ────────────────────────────────────────
// Обновление токена может стартовать из нескольких мест одновременно
// (UserAuthContext.doRefresh при монтировании и adminApi.tryRefresh после 401).
// При rotateRefreshToken на провайдере два параллельных refresh-гранта фатальны:
// второй получает invalid_grant, провайдер расценивает это как повторное
// использование украденного токена и отзывает весь грант (полный re-login).
// Поэтому все вызовы ждут ОДИН общий signinSilent.
let _renewInFlight: Promise<User | null> | null = null;

/** Токен с таким запасом жизни уже обновила другая вкладка. Таймер
 *  addAccessTokenExpiring срабатывает за 60 с до истечения — запас больше. */
const FRESH_TOKEN_MIN_SEC = 120;

// Пользователь лежит в общем localStorage, и таймеры обновления во всех
// вкладках срабатывают в одну секунду. Single-flight выше живёт внутри одной
// вкладки, поэтому вкладки отправляли один и тот же refresh-токен, провайдер
// видел повторное использование и отзывал грант — админку выбивало ровно раз
// в час. Блокировка общая для вкладок; внутри неё сначала перечитываем
// хранилище — getUser заодно перезаводит таймеры этой вкладки на новый токен.
async function renewAcrossTabs(): Promise<User | null> {
  const run = async (): Promise<User | null> => {
    const stored = await userManager.getUser();
    if (stored && !stored.expired && (stored.expires_in ?? 0) > FRESH_TOKEN_MIN_SEC) return stored;
    return userManager.signinSilent();
  };
  if (!navigator.locks) return run();
  return navigator.locks.request('amazing-oidc-renew', run);
}

/** Тихое обновление токена с дедупликацией одновременных вызовов. */
export function renewTokenSilent(): Promise<User | null> {
  // Под игроком обновлять нечем, а тихий вход вернул бы токен самого админа
  if (isImpersonating()) return Promise.reject(new ImpersonationEndedError());
  if (!_renewInFlight) {
    _renewInFlight = renewAcrossTabs()
      .finally(() => { _renewInFlight = null; });
  }
  return _renewInFlight;
}

/** Синхронизирует access/refresh-токены в localStorage для fetch-клиентов. */
function persistTokens(user: User | null): void {
  if (user?.access_token) localStorage.setItem(USER_TOKEN_KEY, user.access_token);
  else                    localStorage.removeItem(USER_TOKEN_KEY);

  if (user?.refresh_token) localStorage.setItem(USER_REFRESH_TOKEN_KEY, user.refresh_token);
  else                     localStorage.removeItem(USER_REFRESH_TOKEN_KEY);
}

userManager.events.addUserLoaded(persistTokens);
userManager.events.addUserUnloaded(() => persistTokens(null));
userManager.events.addAccessTokenExpiring(() => { void renewTokenSilent().catch(() => {}); });
userManager.events.addSilentRenewError(() => { /* токен протух — следующий вызов уйдёт на /authorize */ });

/** Ключ, под которым oidc-client-ts хранит пользователя (WebStorageStateStore). */
const OIDC_USER_KEY = `oidc.user:${ISSUER}:${CLIENT_ID}`;

/**
 * Есть ли сохранённый вход, синхронно и без запросов. Нужен ссылкам на кабинет:
 * без входа сразу уходим на портал, не открывая панель ради одной проверки.
 * Протухший access-токен при живом refresh-токене — тоже вход: его обновят тихо.
 */
export function hasStoredSession(): boolean {
  try {
    const raw = window.localStorage.getItem(OIDC_USER_KEY);
    if (!raw) return false;
    const u = JSON.parse(raw) as { refresh_token?: string; expires_at?: number };
    if (u.refresh_token) return true;
    return !!u.expires_at && u.expires_at * 1000 > Date.now();
  } catch {
    return false;
  }
}

/** Заранее подтянуть discovery провайдера: редирект на вход тогда без задержки. */
export function prefetchOidcMetadata(): void {
  void userManager.metadataService.getMetadata().catch(() => {});
}

/**
 * Токен с запасом жизни, при необходимости тихо обновлённый. Таймер
 * addAccessTokenExpiring в фоновой вкладке браузер притормаживает, поэтому
 * при возврате во вкладку проверяем сами, до первых запросов.
 */
export async function ensureFreshToken(minSec = FRESH_TOKEN_MIN_SEC): Promise<User | null> {
  const user = await userManager.getUser();
  if (!user) return null;
  if (!user.expired && (user.expires_in ?? 0) > minSec) return user;
  return renewTokenSilent();
}

/**
 * Старт авторизации (редирект на портал amazing_auth).
 * `forcePrompt` = true принудительно показывает форму входа (prompt=login),
 * даже при активной сессии — используется для дозавершения онбординга
 * (например, подтверждение email у пользователя со статусом email_unverified).
 */
export async function startLogin(returnTo?: string, forcePrompt = false): Promise<void> {
  if (isImpersonating()) { endImpersonation(); return; }
  await userManager.signinRedirect({
    state:  { returnTo: returnTo ?? window.location.pathname },
    ...(forcePrompt ? { prompt: 'login' } : {}),
  });
}

/**
 * Старт регистрации: тот же OIDC-флоу, но с параметром register=1 — портал
 * сразу показывает форму регистрации внутри interaction. По завершении
 * (после подтверждения email) пользователь получает настоящую OIDC-сессию.
 */
export async function startRegister(returnTo?: string): Promise<void> {
  if (isImpersonating()) { endImpersonation(); return; }
  await userManager.signinRedirect({
    state:            { returnTo: returnTo ?? '/panel' },
    extraQueryParams: { register: '1' },
  });
}

/** Обработка возврата с портала (страница /auth/callback). */
export async function completeLogin(): Promise<{ returnTo: string }> {
  const user = await userManager.signinRedirectCallback();
  persistTokens(user);
  const state = (user.state as { returnTo?: string } | undefined);
  return { returnTo: state?.returnTo ?? '/panel' };
}

/** RP-initiated logout: завершает сессию и на провайдере. */
export async function startLogout(): Promise<void> {
  if (isImpersonating()) { endImpersonation(); return; }
  // Сохраняем id_token ДО очистки — он нужен как id_token_hint, иначе провайдер
  // не сможет завершить сессию/проверить post_logout_redirect_uri.
  let idToken: string | undefined;
  try {
    const user = await userManager.getUser();
    idToken = user?.id_token ?? undefined;
  } catch { /* ignore */ }

  persistTokens(null);
  // Черновики форм принадлежат этому аккаунту — следующему не показываем
  clearAllDrafts();
  await userManager.signoutRedirect(idToken ? { id_token_hint: idToken } : undefined);
}

/** Текущий валидный access-токен (или null), с тихим обновлением при необходимости. */
export async function getValidAccessToken(): Promise<string | null> {
  let user = await userManager.getUser();
  if (user && !user.expired) return user.access_token;
  try {
    user = await renewTokenSilent();
    return user?.access_token ?? null;
  } catch {
    return null;
  }
}
